// src/components/IdeaHubView.tsx
import React, { useState } from 'react';
import type { ProjectState } from '../types/project';
import { getProjectTitle, getTargetWordCount } from '../utils/projectUtils';
import { labelClass, textareaClass, inputClass, primaryButtonClass } from '../constants';
import { useAppContext } from '../contexts/AppContext';
import { 
    useViewEditMode, 
    ViewEditSection, 
    SaveCancelButtons, 
    DisplayField, 
    DisplayGrid 
} from '../utils/viewEditUtils';
import OptimizedBlueprintButton from './OptimizedBlueprintButton';
import { useOptimizedSave } from '../hooks/useOptimizedSave';

interface IdeaHubViewProps {
    // BLUEPRINT MIGRATION: Replace worldBuildingData with blueprint
    projectState: Pick<ProjectState, 'outlineIdea' | 'targetPageCount' | 'targetWordCount' | 'bookMetadata' | 'hasInitialBlueprint' | 'blueprint'> & {
        workType?: string; // Make workType optional since it's not in ProjectState
    };
    onOutlineIdeaChange: (value: string) => void;
    onWorkTypeChange: (value: string) => void;
    onTargetWordCountChange: (value: string) => void; 
    onTargetPageCountChange: (value: string) => void;
    onStoryTitleChange: (title: string) => void; // Add title change handler
    onGenerateInitialBlueprint: () => void; // Renamed from onGenerateBlueprint
    onRegenerateBlueprint: () => void; // Add regenerate option
    onGenerateWithModel?: (modelId: string) => void; // Add model selection handler
    isLoading: boolean;
    creditBalance: number; // Add creditBalance prop
    requiredCredits: number; // Add requiredCredits prop
    lastError?: string; // Add error handling
    failedModel?: string; // Add failed model tracking
}

const workTypeOptions = ["Novel", "Short Story", "Novella", "Article", "LinkedIn Post", "Blog Post", "Research Paper", "Other"];

const IdeaHubView: React.FC<IdeaHubViewProps> = ({
    projectState,
    onOutlineIdeaChange,
    onWorkTypeChange,
    onTargetWordCountChange,
    onTargetPageCountChange,
    onStoryTitleChange, // Add title change handler
    onGenerateInitialBlueprint, // Renamed
    onRegenerateBlueprint, // Add regenerate handler
    onGenerateWithModel, // Add model selection handler
    isLoading,
    creditBalance, // Destructure creditBalance
    requiredCredits, // Destructure requiredCredits
    lastError, // Add error handling
    failedModel, // Add failed model tracking
}) => {
    const appContext = useAppContext();
    const { dbProject } = appContext;
    const { activeEditSection, handleEditToggle, handleSaveAndExit, handleCancelEdit } = useViewEditMode();
    const { saveIdeaHub } = useOptimizedSave();
    const [isIdeaExpanded, setIsIdeaExpanded] = useState(false);

    // Get blueprint state management functions from optimized data context
    const { blueprint } = appContext.optimizedData || {};

    const handleSaveChanges = async () => {
        try {
            // Use optimized save that sends only idea hub specific data
            await saveIdeaHub.mutateAsync({
                projectId: dbProject?.uuid || dbProject?.id?.toString() || '',
                idea: projectState.outlineIdea,
                workType: projectState.workType,
                targetWordCount: projectState.targetWordCount,
                setBlueprintSaving: blueprint?.setBlueprintSaving
            });
            handleSaveAndExit();
        } catch (error) {
            console.error('Failed to save changes:', error);
        }
    };
    
    // Check if idea is long enough to need expansion
    const ideaLength = projectState.outlineIdea.length;
    const shouldShowExpandButton = ideaLength > 200;
    const renderEditForm = () => (
        <div className="space-y-4">
            <div>
                <label htmlFor="outlineIdea" className={`${labelClass} text-lg`}>Your Core Idea / Topic:</label>
                <textarea
                    id="outlineIdea"
                    value={projectState.outlineIdea}
                    onChange={(e) => onOutlineIdeaChange(e.target.value)}
                    placeholder="e.g., A sci-fi epic about a lost colony searching for Earth, a comprehensive guide to sustainable gardening, an analysis of post-modern literature..."
                    className={`${textareaClass} min-h-[120px] text-base`}
                    rows={5}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                    <label htmlFor="workType" className={labelClass}>Type of Work:</label>
                    <select
                        id="workType"
                        value={projectState.workType || 'Novel'}
                        onChange={(e) => onWorkTypeChange(e.target.value)}
                        className={inputClass}
                    >
                        {workTypeOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="targetWordCount" className={labelClass}>Target Word Count (approx.):</label>
                    <input
                        id="targetWordCount"
                        type="number"
                        value={projectState.targetWordCount !== undefined ? projectState.targetWordCount : getTargetWordCount(dbProject)}
                        onChange={(e) => onTargetWordCountChange(e.target.value)}
                        placeholder="e.g., 80000 for a novel"
                        className={inputClass}
                        min="0"
                    />
                </div>
            </div>
             <div>
                <label htmlFor="targetPageCount" className={labelClass}>Target Page Count (Optional, approx.):</label>
                <input
                    id="targetPageCount"
                    type="number" 
                    value={projectState.targetPageCount || ''}
                    onChange={(e) => onTargetPageCountChange(e.target.value)}
                    placeholder="e.g., 300"
                    className={inputClass}
                    min="0"
                />
            </div>
        </div>
    );

    const renderDisplayView = () => (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Core Idea / Topic</label>
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border">
                    <div className="overflow-hidden transition-all duration-300 ease-in-out">
                        <p className="text-gray-800 leading-relaxed">
                            {shouldShowExpandButton && !isIdeaExpanded 
                                ? `${projectState.outlineIdea.substring(0, 200)}...`
                                : projectState.outlineIdea
                            }
                        </p>
                    </div>
                    {shouldShowExpandButton && (
                        <button
                            onClick={() => setIsIdeaExpanded(!isIdeaExpanded)}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none focus:underline transition-all duration-200 ease-in-out transform hover:scale-105"
                        >
                            {isIdeaExpanded ? 'Show Less' : 'Show More'}
                        </button>
                    )}
                </div>
            </div>
            <DisplayGrid>
                <DisplayField
                    label="Type of Work"
                    value={projectState.workType}
                    background={true}
                />
                <DisplayField
                    label="Target Word Count"
                    value={projectState.targetWordCount !== undefined ? projectState.targetWordCount : getTargetWordCount(dbProject)}
                    background={true}
                />
            </DisplayGrid>
            <DisplayField
                label="Target Page Count"
                value={projectState.targetPageCount}
                background={true}
            />
        </div>
    );

    return (
        <div className="w-full max-w-4xl mx-auto p-3 sm:p-6 md:p-8 bg-white rounded-xl shadow-2xl space-y-4 sm:space-y-6 overflow-hidden" style={{backgroundImage: "url('/assets/texture_tile.png')", backgroundRepeat: 'repeat', backgroundSize: 'auto'}}>
            {/* Keep a button to print project state in a console log */}
            <button
                onClick={() => console.log('Project State:', projectState)}
                className="hidden"
            >
                Print Project State
            </button>    

            {projectState.hasInitialBlueprint ? (
                <>
                    <h1 className="text-2xl sm:text-3xl font-bold text-center text-gray-800">Your Project Overview</h1>
                    <p className="text-center text-gray-600 text-sm sm:text-base">
                        Blueprint generated successfully! Review your project details below. All tabs are now activated - 
                        explore "Book Details", "World Building", "Chapters Building" and other sections to develop your story.
                    </p>
                </>
            ) : (
                <>
                

                    <h1 className="text-2xl sm:text-3xl font-bold text-center text-gray-800">Start Your Masterpiece-IdeaHub</h1>
                    <p className="text-center text-gray-600 text-sm sm:text-base">
                        Tell us about your idea, and we'll help you craft a foundational blueprint including
                        overall book details and initial world-building concepts. Chapter outlines can be generated in the "Chapters Building" tab afterwards.
                    </p>
                </>
            )}  

            {/* Generated Title Display - Moved above Project Details */}
            {/* BLUEPRINT MIGRATION: Use blueprint.worldBuildingSuggestions instead of worldBuildingData */}
            {projectState.hasInitialBlueprint && projectState.blueprint?.worldBuildingSuggestions?.storyTitleIdeas && projectState.blueprint.worldBuildingSuggestions.storyTitleIdeas?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                    <div className="space-y-3">
                        <div className="text-center">
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
                                {getProjectTitle(dbProject, projectState.blueprint.worldBuildingSuggestions.selectedStoryTitle || projectState.blueprint.worldBuildingSuggestions.storyTitleIdeas?.[0])}
                            </h2>
                            <p className="text-sm text-blue-600 font-medium">Project Title</p>
                        </div>
                        {projectState.blueprint.worldBuildingSuggestions.storyTitleIdeas?.length > 1 && (
                            <div className="text-center">
                                <details className="inline-block">
                                    <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm font-medium">
                                        View Alternative Titles ({(projectState.blueprint.worldBuildingSuggestions.storyTitleIdeas?.length || 0) - 1})
                                    </summary>
                                    <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-64 z-10">
                                        <p className="text-sm text-gray-600 mb-2">Click to select a different title:</p>
                                        {projectState.blueprint.worldBuildingSuggestions.storyTitleIdeas?.map((title, index) => (
                                            <button
                                                key={index}
                                                onClick={() => onStoryTitleChange(title)}
                                                className={`block w-full text-left p-2 rounded hover:bg-blue-50 transition-colors ${
                                                    getProjectTitle(dbProject, projectState.blueprint.worldBuildingSuggestions.selectedStoryTitle || projectState.blueprint.worldBuildingSuggestions.storyTitleIdeas?.[0]) === title
                                                        ? 'bg-blue-100 text-blue-800 font-medium'
                                                        : 'text-gray-700'
                                                }`}
                                            >
                                                {title}
                                                {index === 0 && !projectState.blueprint.worldBuildingSuggestions.selectedStoryTitle && (
                                                    <span className="ml-2 text-xs text-blue-600">(current)</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </details>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ViewEditSection
                title="Project Details"
                subtitle="Core idea and project specifications"
                isActive={activeEditSection === 'project'}
                onEditToggle={() => handleEditToggle('project')}
                className="bg-gray-50"
            >
                {activeEditSection === 'project' ? (
                    <>
                        {renderEditForm()}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView()
                )}
            </ViewEditSection>

            {/* 🚀 OPTIMIZED BLUEPRINT BUTTON - Three-State System with Model Selection */}
            {projectState.hasInitialBlueprint ? (
                <div className="text-center text-gray-600 text-sm sm:text-base mt-6">
                    <p>You can edit these details by clicking the "Edit" button above.</p>
                </div>
            ) : (
                <OptimizedBlueprintButton
                    blueprintExists={projectState.hasInitialBlueprint}
                    isLoading={isLoading}
                    creditBalance={creditBalance}
                    requiredCredits={requiredCredits}
                    outlineIdea={projectState.outlineIdea}
                    onGenerateInitial={onGenerateInitialBlueprint}
                    onRegenerate={onRegenerateBlueprint}
                    onGenerateWithModel={onGenerateWithModel}
                    userPlan={'FREE'}
                    lastError={lastError}
                    failedModel={failedModel}
                />
            )}
        </div>
    );
};

export default IdeaHubView;
